import { useEffect, useRef, useState } from "react";

import { useTaskCreationFlow } from "../Contexts/TaskCreationFlow";

/** How long the pill stays up after a save. Long enough to read, short enough
 * not to sit there as if it were part of the panel. */
const VISIBLE_MS = 2500;

export interface IUseDraftSavedPillResult {
  visible: boolean;
  /** Bumped per save so the pill replays its entrance when one lands while it
   * is still on screen. */
  replayToken: number;
}

/**
 * Drives the "Draft saved" pill from the draft itself.
 *
 * A successful save is the only thing that writes a new draft object into the
 * cache (`useSaveScreen`'s `onSuccess`), so watching that object's identity
 * catches every save and no failure — where watching a "is saving" flag fall
 * back to false would flash the pill on a failed one too.
 *
 * The first draft to arrive is the fetch, not a save, so it is swallowed.
 */
export const useDraftSavedPill = (): IUseDraftSavedPillResult => {
  const { draft } = useTaskCreationFlow();
  const previousDraft = useRef(draft);
  const [visible, setVisible] = useState(false);
  const [replayToken, setReplayToken] = useState(0);
  const hideTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const previous = previousDraft.current;
    previousDraft.current = draft;

    if (!draft || !previous || previous === draft) return;

    setVisible(true);
    setReplayToken((token) => token + 1);

    clearTimeout(hideTimeout.current);
    hideTimeout.current = setTimeout(() => setVisible(false), VISIBLE_MS);
  }, [draft]);

  useEffect(() => () => clearTimeout(hideTimeout.current), []);

  return { visible, replayToken };
};
